/**
 * Pestaña 1 de la vista People: "Comparativa" (doc 14/09 §2.2). Resumen
 * global (cumplimiento New Clappers / adopción voluntaria), cumplimiento y
 * adopción por área (tiles + gauges del área elegida) y por país, y el
 * resumen por líder con el equipo desplegable — la guía visual es el HTML de
 * Andrea (dashboard-onboarding-winclap-v9_3).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  adopcionVoluntaria, cumplimientoNewClappers, metricsBy, type GroupMetrics, type LearningDashboardRow,
} from '@/../../shared/people-learning';
import { StatCard } from '@/components/ui/stat-card';
import { Gauge } from './Gauge';
import { STATUS_BADGE, TONE_HEX, TONE_KPI, TONE_TILE, fmtDate, pctTone, statusLabel } from './learning-dash-utils';
import { cn } from '@/lib/utils';
import { usePagination } from '@/hooks/usePagination';

export function ComparativaTab({ rows, launchDate, today, onDetail }: {
  rows: LearningDashboardRow[];
  launchDate: string;
  today: string;
  onDetail: (email: string) => void;
}) {
  const { t } = useTranslation();
  const k = 'people_learning.dash.comparativa';
  const cumpl = cumplimientoNewClappers(rows, today);
  const adop = adopcionVoluntaria(rows);

  const byArea = useMemo(() => metricsBy(rows, (r) => ({ key: r.area || '—', label: r.area || t(`${k}.no_area`, 'Sin área') }), today), [rows, today, t]);
  const byCountry = useMemo(() => metricsBy(rows, (r) => ({ key: r.country || '—', label: r.country || t(`${k}.no_country`, 'Sin país') }), today), [rows, today, t]);
  const byLeader = useMemo(
    () => metricsBy(rows.filter((r) => r.leaderName), (r) => ({ key: r.leaderEmail ?? r.leaderName, label: r.leaderName }), today)
      .sort((a, b) => b.total - a.total),
    [rows, today]
  );
  const [areaKey, setAreaKey] = useState<string | null>(null);
  const selectedArea = byArea.find((a) => a.key === areaKey) ?? byArea[0] ?? null;
  const [leaderSearch, setLeaderSearch] = useState('');
  const [openLeader, setOpenLeader] = useState<GroupMetrics | null>(null);
  const leadersShown = byLeader.filter((l) => l.label.toLowerCase().includes(leaderSearch.trim().toLowerCase()));
  const { pageItems: pageLeaders, footer: paginationFooter } = usePagination(leadersShown, 25);

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2">
        <StatCard
          label={t(`${k}.kpi_cumplimiento`, 'Cumplimiento New Clappers')}
          value={`${cumpl.pct}%`}
          fill={cumpl.total ? TONE_KPI[pctTone(cumpl.pct)] : 'neutral'}
          sub={t(`${k}.kpi_cumplimiento_hint`, { done: cumpl.done, total: cumpl.total, late: cumpl.late, pending: cumpl.pending, date: fmtDate(launchDate), defaultValue: '{{done}} de {{total}} ingresos desde el {{date}} completaron a tiempo · {{late}} tarde · {{pending}} aún en plazo' })}
        />
        <StatCard
          label={t(`${k}.kpi_adopcion`, 'Adopción voluntaria (Old Clappers)')}
          value={`${adop.pct}%`}
          sub={t(`${k}.kpi_adopcion_hint`, { done: adop.done, total: adop.total, defaultValue: '{{done}} de {{total}} completaron el 100% sin ser obligatorio' })}
        />
      </div>

      {/* Por área: tiles (cumplimiento) + gauges del área elegida */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold">{t(`${k}.by_area`, 'Cumplimiento y adopción por área')}</h3>
        <div className="grid gap-4 lg:grid-cols-[3fr_2fr]">
          <Card className="p-4">
            <p className="mb-3 text-xs font-semibold text-muted-foreground">{t(`${k}.area_tiles`, 'Cumplimiento por área (clic para ver el detalle)')}</p>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-4">
              {byArea.map((a) => {
                const tone = pctTone(a.cumplimiento.pct);
                return (
                  <button
                    key={a.key}
                    onClick={() => setAreaKey(a.key)}
                    className={cn('flex aspect-square flex-col justify-between rounded-2xl border p-3 text-left transition hover:scale-[1.03] hover:shadow-md', TONE_TILE[tone], selectedArea?.key === a.key && 'ring-2 ring-primary')}
                  >
                    <span className="text-[11px] font-semibold leading-tight">{a.label}</span>
                    <span>
                      <span className="block text-2xl font-bold tabular-nums">{a.cumplimiento.pct}%</span>
                      <span className="text-[10px] opacity-80">{t(`${k}.tile_sub`, { n: a.newClappers, total: a.total, defaultValue: '{{n}} new · {{total}} en total' })}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </Card>
          <Card className="flex flex-col items-center justify-center p-4">
            <p className="mb-3 text-xs font-semibold text-muted-foreground">{selectedArea ? selectedArea.label : t(`${k}.area_detail`, 'Detalle por área')}</p>
            {selectedArea && (
              <div className="flex flex-wrap items-end justify-center gap-6">
                <Gauge value={selectedArea.cumplimiento.pct} color={TONE_HEX[pctTone(selectedArea.cumplimiento.pct)]} label={t(`${k}.gauge_cumplimiento`, 'Cumplimiento')} />
                <Gauge value={selectedArea.adopcion.pct} color="#CEABFF" label={t(`${k}.gauge_adopcion`, 'Adopción voluntaria')} />
              </div>
            )}
            {selectedArea && (
              <p className="mt-3 text-center text-[11px] text-muted-foreground">
                {t(`${k}.area_numbers`, { done: selectedArea.cumplimiento.done, total: selectedArea.cumplimiento.total, adopDone: selectedArea.adopcion.done, adopTotal: selectedArea.adopcion.total, defaultValue: 'New Clappers a tiempo: {{done}}/{{total}} · Old Clappers completados: {{adopDone}}/{{adopTotal}}' })}
              </p>
            )}
          </Card>
        </div>
      </section>

      {/* Por país: mini barras cumplimiento / adopción */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold">{t(`${k}.by_country`, 'Cumplimiento y adopción por país')}</h3>
        <Card className="p-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {byCountry.map((c) => (
              <div key={c.key}>
                <div className="h-36">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[{ name: t(`${k}.bar_cumpl`, 'Cumpl.'), v: c.cumplimiento.pct, fill: '#1CC9B9' }, { name: t(`${k}.bar_adop`, 'Adop.'), v: c.adopcion.pct, fill: '#CEABFF' }]} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis hide domain={[0, 100]} />
                      <Tooltip formatter={(v) => `${v}%`} cursor={{ fill: 'transparent' }} />
                      <Bar dataKey="v" radius={[6, 6, 0, 0]} maxBarSize={26} label={{ position: 'top', fontSize: 11, formatter: (v) => `${v}%` }} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="mt-1 text-center text-xs font-semibold text-muted-foreground">{c.label}</p>
                <p className="text-center text-[10px] text-muted-foreground">{t(`${k}.tile_sub`, { n: c.newClappers, total: c.total, defaultValue: '{{n}} new · {{total}} en total' })}</p>
              </div>
            ))}
          </div>
        </Card>
      </section>

      {/* Resumen por líder */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold">{t(`${k}.by_leader`, 'Resumen por líder')}</h3>
        <Input value={leaderSearch} onChange={(e) => setLeaderSearch(e.target.value)} placeholder={t(`${k}.leader_search`, 'Buscar líder por nombre…')} className="h-8 w-64 text-xs" />
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t(`${k}.col_leader`, 'Líder')}</TableHead>
                <TableHead className="text-right">{t(`${k}.col_total`, 'Total a cargo')}</TableHead>
                <TableHead className="text-right">{t(`${k}.col_cumpl`, 'Cumplimiento New Clappers')}</TableHead>
                <TableHead className="text-right">{t(`${k}.col_adop`, 'Adopción voluntaria')}</TableHead>
                <TableHead className="text-right">{t(`${k}.col_detail`, 'Detalle')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leadersShown.length === 0 && (
                <TableRow><TableCell colSpan={5} className="py-6 text-center text-muted-foreground">{t(`${k}.no_leaders`, 'Sin líderes para mostrar.')}</TableCell></TableRow>
              )}
              {pageLeaders.map((l) => (
                <TableRow key={l.key}>
                  <TableCell className="font-medium">{l.label}</TableCell>
                  <TableCell className="text-right tabular-nums">{l.total}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {l.cumplimiento.total > 0 ? <span style={{ color: TONE_HEX[pctTone(l.cumplimiento.pct)] }} className="font-semibold">{l.cumplimiento.pct}%</span> : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{l.adopcion.total > 0 ? `${l.adopcion.pct}%` : '—'}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setOpenLeader(l)}>{t(`${k}.see_team`, 'Ver equipo →')}</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
        {paginationFooter}
      </section>

      <Sheet open={!!openLeader} onOpenChange={(o) => { if (!o) setOpenLeader(null); }}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{openLeader ? t(`${k}.team_of`, { name: openLeader.label, defaultValue: '{{name}} · Mi equipo' }) : ''}</SheetTitle>
          </SheetHeader>
          {openLeader && (
            <div className="mt-4 divide-y rounded-md border">
              {rows
                .filter((r) => (r.leaderEmail ?? r.leaderName) === openLeader.key)
                .sort((a, b) => a.name.localeCompare(b.name, 'es'))
                .map((r) => (
                  <button key={r.email} onClick={() => onDetail(r.email)} className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-muted/60">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{r.name}</p>
                      <p className="truncate text-xs text-muted-foreground">{[r.role, r.country].filter(Boolean).join(' · ')}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[r.status]}`}>{statusLabel(t, r.status)}</span>
                      <p className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">{Math.round(r.progressPct * 100)}%</p>
                    </div>
                  </button>
                ))}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
